import { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { MenuGuard } from '@/auth/MenuGuard';
import { AppLayout } from '@/layout/AppLayout';
import { DashboardPage } from '@/pages/DashboardPage';
import { LoginPage } from '@/pages/LoginPage';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ROUTES } from '@/lib/routes';

// ── Lazy-loaded pages (code-split) ──
const EntityRegistryPage = lazy(() =>
  import('@/pages/entity-registry/EntityRegistryPage').then((m) => ({ default: m.EntityRegistryPage })),
);
const EntityRegistryEditor = lazy(() =>
  import('@/pages/entity-registry/EntityRegistryEditor').then((m) => ({ default: m.EntityRegistryEditor })),
);
const EntityRegistryNewPage = lazy(() =>
  import('@/pages/entity-registry/EntityRegistryNewPage').then((m) => ({ default: m.EntityRegistryNewPage })),
);
const EntityDependencyGraph = lazy(() =>
  import('@/pages/entity-registry/EntityDependencyGraph').then((m) => ({ default: m.EntityDependencyGraph })),
);
const MappingsListPage = lazy(() =>
  import('@/pages/mappings/MappingsListPage').then((m) => ({ default: m.MappingsListPage })),
);
const MappingEditorPage = lazy(() =>
  import('@/pages/mappings/MappingEditorPage').then((m) => ({ default: m.MappingEditorPage })),
);
const MappingNewPage = lazy(() =>
  import('@/pages/mappings/MappingNewPage').then((m) => ({ default: m.MappingNewPage })),
);
const SchemasListPage = lazy(() =>
  import('@/pages/schemas/SchemasListPage').then((m) => ({ default: m.SchemasListPage })),
);
const SchemaEditorPage = lazy(() =>
  import('@/pages/schemas/SchemaEditorPage').then((m) => ({ default: m.SchemaEditorPage })),
);
const SchemaNewPage = lazy(() =>
  import('@/pages/schemas/SchemaNewPage').then((m) => ({ default: m.SchemaNewPage })),
);
const LookupTablesListPage = lazy(() =>
  import('@/pages/lookup-tables/LookupTablesListPage').then((m) => ({ default: m.LookupTablesListPage })),
);
const LookupTableEditorPage = lazy(() =>
  import('@/pages/lookup-tables/LookupTableEditorPage').then((m) => ({ default: m.LookupTableEditorPage })),
);
const LookupTableNewPage = lazy(() =>
  import('@/pages/lookup-tables/LookupTableNewPage').then((m) => ({ default: m.LookupTableNewPage })),
);
const UomConversionsListPage = lazy(() =>
  import('@/pages/uom-conversions/UomConversionsListPage').then((m) => ({ default: m.UomConversionsListPage })),
);
const UomConversionNewPage = lazy(() =>
  import('@/pages/uom-conversions/UomConversionNewPage').then((m) => ({ default: m.UomConversionNewPage })),
);
const UomConversionEditorPage = lazy(() =>
  import('@/pages/uom-conversions/UomConversionEditorPage').then((m) => ({ default: m.UomConversionEditorPage })),
);
const SetupChecklistPage = lazy(() =>
  import('@/pages/setup/SetupChecklistPage').then((m) => ({ default: m.SetupChecklistPage })),
);
const ConnectionsPage = lazy(() =>
  import('@/pages/connections/ConnectionsPage').then((m) => ({ default: m.ConnectionsPage })),
);
const SystemsPage = lazy(() =>
  import('@/pages/systems/SystemsPage').then((m) => ({ default: m.SystemsPage })),
);
const WorkflowPage = lazy(() =>
  import('@/pages/workflows/WorkflowPage').then((m) => ({ default: m.WorkflowPage })),
);
const DlqPage = lazy(() =>
  import('@/pages/dlq/DlqPage').then((m) => ({ default: m.DlqPage })),
);
const UsersPage = lazy(() =>
  import('@/pages/users/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const CompaniesPage = lazy(() =>
  import('@/pages/companies/CompaniesPage').then((m) => ({ default: m.CompaniesPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const DemoInboxPage = lazy(() =>
  import('@/pages/demo-inbox/DemoInboxPage').then((m) => ({ default: m.DemoInboxPage })),
);
const QuotaPage = lazy(() =>
  import('@/pages/quota/QuotaPage').then((m) => ({ default: m.QuotaPage })),
);
const AuditTrailPage = lazy(() =>
  import('@/pages/audit-trail/AuditTrailPage').then((m) => ({ default: m.AuditTrailPage })),
);
const IntegrationAuditPage = lazy(() =>
  import('@/pages/integration-audit/IntegrationAuditPage').then((m) => ({ default: m.IntegrationAuditPage })),
);
const ConfigSyncPage = lazy(() =>
  import('@/pages/config-sync/ConfigSyncPage').then((m) => ({ default: m.ConfigSyncPage })),
);
const PollingStatusPage = lazy(() =>
  import('@/pages/polling/PollingStatusPage').then((m) => ({ default: m.PollingStatusPage })),
);
const InitialLoadPage = lazy(() =>
  import('@/pages/initial-load/InitialLoadPage').then((m) => ({ default: m.InitialLoadPage })),
);
const ReconciliationPage = lazy(() =>
  import('@/pages/reconciliation/ReconciliationPage').then((m) => ({ default: m.ReconciliationPage })),
);
const DataHubOverviewPage = lazy(() =>
  import('@/pages/data-hub/DataHubOverviewPage').then((m) => ({ default: m.DataHubOverviewPage })),
);
const DataHubDashboardsPage = lazy(() =>
  import('@/pages/data-hub/DataHubDashboardsPage').then((m) => ({ default: m.DataHubDashboardsPage })),
);
const DataHubExplorePage = lazy(() =>
  import('@/pages/data-hub/DataHubExplorePage').then((m) => ({ default: m.DataHubExplorePage })),
);
const DataHubReportsPage = lazy(() =>
  import('@/pages/data-hub/DataHubReportsPage').then((m) => ({ default: m.DataHubReportsPage })),
);
const DataHubPowerBiPage = lazy(() =>
  import('@/pages/data-hub/DataHubPowerBiPage').then((m) => ({ default: m.DataHubPowerBiPage })),
);
const DataHubAgentsPage = lazy(() =>
  import('@/pages/data-hub/DataHubAgentsPage').then((m) => ({ default: m.DataHubAgentsPage })),
);
const DataHubTablesPage = lazy(() =>
  import('@/pages/data-hub/DataHubTablesPage').then((m) => ({ default: m.DataHubTablesPage })),
);
const AlertsPage = lazy(() =>
  import('@/pages/alerts/AlertsPage').then((m) => ({ default: m.AlertsPage })),
);
const TermsOfServicePage = lazy(() =>
  import('@/pages/legal/TermsOfServicePage').then((m) => ({ default: m.TermsOfServicePage })),
);
const PrivacyPolicyPage = lazy(() =>
  import('@/pages/legal/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage })),
);
const DataProcessingAgreementPage = lazy(() =>
  import('@/pages/legal/DataProcessingAgreementPage').then((m) => ({ default: m.DataProcessingAgreementPage })),
);
const AcceptableUsePolicyPage = lazy(() =>
  import('@/pages/legal/AcceptableUsePolicyPage').then((m) => ({ default: m.AcceptableUsePolicyPage })),
);
const ServiceLevelAgreementPage = lazy(() =>
  import('@/pages/legal/ServiceLevelAgreementPage').then((m) => ({ default: m.ServiceLevelAgreementPage })),
);
const SecurityPage = lazy(() =>
  import('@/pages/legal/SecurityPage').then((m) => ({ default: m.SecurityPage })),
);

const DocumentsInboxPage = lazy(() =>
  import('@/pages/documents/DocumentsInboxPage').then((m) => ({ default: m.DocumentsInboxPage })),
);
const DocumentReviewPage = lazy(() =>
  import('@/pages/documents/DocumentReviewPage').then((m) => ({ default: m.DocumentReviewPage })),
);
const DocumentInstructionsPage = lazy(() =>
  import('@/pages/documents/DocumentInstructionsPage').then((m) => ({ default: m.DocumentInstructionsPage })),
);

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-12">
      <LoadingSpinner size="lg" />
    </div>
  );
}

/** Wrap a lazy page with Suspense + MenuGuard */
function guarded(routePattern: string, Component: React.LazyExoticComponent<React.ComponentType>) {
  return (
    <MenuGuard routePattern={routePattern}>
      <Suspense fallback={<PageLoader />}>
        <Component />
      </Suspense>
    </MenuGuard>
  );
}

export function App() {
  return (
    <Routes>
      <Route path={ROUTES.LOGIN} element={<LoginPage />} />
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route
          path={ROUTES.DASHBOARD}
          element={
            <MenuGuard routePattern={ROUTES.DASHBOARD}>
              <DashboardPage />
            </MenuGuard>
          }
        />
        <Route path={ROUTES.ENTITY_REGISTRY} element={guarded(ROUTES.ENTITY_REGISTRY, EntityRegistryPage)} />
        <Route path={ROUTES.ENTITY_REGISTRY_NEW} element={guarded(ROUTES.ENTITY_REGISTRY_NEW, EntityRegistryNewPage)} />
        <Route path={ROUTES.ENTITY_REGISTRY_GRAPH} element={guarded(ROUTES.ENTITY_REGISTRY_GRAPH, EntityDependencyGraph)} />
        <Route path={ROUTES.ENTITY_REGISTRY_EDIT} element={guarded(ROUTES.ENTITY_REGISTRY_EDIT, EntityRegistryEditor)} />
        <Route path={ROUTES.MAPPINGS} element={guarded(ROUTES.MAPPINGS, MappingsListPage)} />
        <Route path={ROUTES.MAPPING_NEW} element={guarded(ROUTES.MAPPING_NEW, MappingNewPage)} />
        <Route path={ROUTES.MAPPING_EDIT} element={guarded(ROUTES.MAPPING_EDIT, MappingEditorPage)} />
        <Route path={ROUTES.SCHEMAS} element={guarded(ROUTES.SCHEMAS, SchemasListPage)} />
        <Route path={ROUTES.SCHEMA_NEW} element={guarded(ROUTES.SCHEMA_NEW, SchemaNewPage)} />
        <Route path={ROUTES.SCHEMA_EDIT} element={guarded(ROUTES.SCHEMA_EDIT, SchemaEditorPage)} />
        <Route path={ROUTES.LOOKUP_TABLES} element={guarded(ROUTES.LOOKUP_TABLES, LookupTablesListPage)} />
        <Route path={ROUTES.LOOKUP_TABLE_NEW} element={guarded(ROUTES.LOOKUP_TABLE_NEW, LookupTableNewPage)} />
        <Route path={ROUTES.LOOKUP_TABLE_EDIT} element={guarded(ROUTES.LOOKUP_TABLE_EDIT, LookupTableEditorPage)} />
        <Route path={ROUTES.UOM_CONVERSIONS} element={guarded(ROUTES.UOM_CONVERSIONS, UomConversionsListPage)} />
        <Route path={ROUTES.UOM_CONVERSION_NEW} element={guarded(ROUTES.UOM_CONVERSION_NEW, UomConversionNewPage)} />
        <Route path={ROUTES.UOM_CONVERSION_EDIT} element={guarded(ROUTES.UOM_CONVERSION_EDIT, UomConversionEditorPage)} />
        <Route path={ROUTES.SETUP} element={<Suspense fallback={<PageLoader />}><SetupChecklistPage /></Suspense>} />
        <Route path={ROUTES.CONNECTIONS} element={guarded(ROUTES.CONNECTIONS, ConnectionsPage)} />
        <Route path={ROUTES.SYSTEMS} element={guarded(ROUTES.SYSTEMS, SystemsPage)} />
        <Route path={ROUTES.WORKFLOWS} element={guarded(ROUTES.WORKFLOWS, WorkflowPage)} />
        <Route path={ROUTES.DEAD_LETTER_QUEUES} element={guarded(ROUTES.DEAD_LETTER_QUEUES, DlqPage)} />
        <Route path={ROUTES.USERS} element={guarded(ROUTES.USERS, UsersPage)} />
        <Route path={ROUTES.COMPANIES} element={guarded(ROUTES.COMPANIES, CompaniesPage)} />
        <Route path={ROUTES.SETTINGS} element={guarded(ROUTES.SETTINGS, SettingsPage)} />
        <Route path={ROUTES.DEMO_INBOX} element={guarded(ROUTES.DEMO_INBOX, DemoInboxPage)} />
        <Route path={ROUTES.QUOTA} element={guarded(ROUTES.QUOTA, QuotaPage)} />
        <Route path={ROUTES.AUDIT_TRAIL} element={guarded(ROUTES.AUDIT_TRAIL, AuditTrailPage)} />
        <Route path={ROUTES.INTEGRATION_AUDIT} element={guarded(ROUTES.INTEGRATION_AUDIT, IntegrationAuditPage)} />
        <Route path={ROUTES.CONFIG_SYNC} element={guarded(ROUTES.CONFIG_SYNC, ConfigSyncPage)} />
        <Route path={ROUTES.POLLING_STATUS} element={guarded(ROUTES.POLLING_STATUS, PollingStatusPage)} />
        <Route path={ROUTES.INITIAL_LOAD} element={guarded(ROUTES.INITIAL_LOAD, InitialLoadPage)} />
        <Route path={ROUTES.INITIAL_LOAD_DETAIL} element={guarded(ROUTES.INITIAL_LOAD_DETAIL, InitialLoadPage)} />
        <Route path={ROUTES.RECONCILIATION} element={guarded(ROUTES.RECONCILIATION, ReconciliationPage)} />
        <Route path={ROUTES.DATA_HUB} element={guarded(ROUTES.DATA_HUB, DataHubOverviewPage)} />
        <Route path={ROUTES.DATA_HUB_DASHBOARDS} element={guarded(ROUTES.DATA_HUB_DASHBOARDS, DataHubDashboardsPage)} />
        <Route path={ROUTES.DATA_HUB_EXPLORE} element={guarded(ROUTES.DATA_HUB_EXPLORE, DataHubExplorePage)} />
        <Route path={ROUTES.DATA_HUB_REPORTS} element={guarded(ROUTES.DATA_HUB_REPORTS, DataHubReportsPage)} />
        <Route path={ROUTES.DATA_HUB_AGENTS} element={guarded(ROUTES.DATA_HUB_AGENTS, DataHubAgentsPage)} />
        <Route path={ROUTES.DATA_HUB_POWERBI} element={guarded(ROUTES.DATA_HUB_POWERBI, DataHubPowerBiPage)} />
        <Route path={ROUTES.DATA_HUB_TABLES} element={guarded(ROUTES.DATA_HUB_TABLES, DataHubTablesPage)} />
        <Route path={ROUTES.DOCUMENTS} element={guarded(ROUTES.DOCUMENTS, DocumentsInboxPage)} />
        <Route path={ROUTES.DOCUMENTS_INSTRUCTIONS} element={guarded(ROUTES.DOCUMENTS_INSTRUCTIONS, DocumentInstructionsPage)} />
        <Route path={ROUTES.DOCUMENT_REVIEW} element={guarded(ROUTES.DOCUMENT_REVIEW, DocumentReviewPage)} />
        <Route path={ROUTES.ALERTS} element={guarded(ROUTES.ALERTS, AlertsPage)} />
        <Route path={ROUTES.TERMS_OF_SERVICE} element={<Suspense fallback={<PageLoader />}><TermsOfServicePage /></Suspense>} />
        <Route path={ROUTES.PRIVACY_POLICY} element={<Suspense fallback={<PageLoader />}><PrivacyPolicyPage /></Suspense>} />
        <Route path={ROUTES.DATA_PROCESSING_AGREEMENT} element={<Suspense fallback={<PageLoader />}><DataProcessingAgreementPage /></Suspense>} />
        <Route path={ROUTES.ACCEPTABLE_USE_POLICY} element={<Suspense fallback={<PageLoader />}><AcceptableUsePolicyPage /></Suspense>} />
        <Route path={ROUTES.SERVICE_LEVEL_AGREEMENT} element={<Suspense fallback={<PageLoader />}><ServiceLevelAgreementPage /></Suspense>} />
        <Route path={ROUTES.SECURITY} element={<Suspense fallback={<PageLoader />}><SecurityPage /></Suspense>} />
      </Route>
    </Routes>
  );
}
